/**
 * Message content filter — prevents disintermediation and profanity.
 *
 * Detects and redacts:
 * - Phone numbers (international + local, +503, spelled out)
 * - Email addresses (standard + obfuscated: [at], (at), etc.)
 * - URLs (http/https/www + common TLDs)
 * - Social platform keywords (WhatsApp, Telegram, etc.)
 * - Basic profanity (Spanish + English)
 *
 * Approach: redact with [***], never block sending.
 * Like Airbnb — all communication stays on-platform.
 */

import { PII_EMAIL_PATTERNS, PII_PHONE_PATTERNS, redactPatterns } from './pii-patterns'

const REDACTION = '[***]'

// ─── Phone + email patterns ──────────────────────────────────────────────
//
// Both live in `pii-patterns.ts`. They used to be declared here and a
// weaker copy lived in `sanitize.ts`; Phase 4 collapsed the two into one
// set so a number redacted in chat is also redacted in telemetry.

// ─── URL patterns ─────────────────────────────────────────────────────────

const URL_PATTERNS: RegExp[] = [
  // Standard URLs
  /(https?:\/\/|www\.)[^\s]+/gi,
  // Common TLDs without protocol
  /\b[\w.-]+\.(com|net|org|io|sv|app|co|me|dev|xyz)\b/gi,
]

// ─── Social platform keywords ─────────────────────────────────────────────

const SOCIAL_PATTERNS: RegExp[] = [
  /\b(whatsapp|whats\s*app|telegram|instagram|facebook|messenger|signal|tiktok|snapchat|wsp|wa\.me)\b/gi,
]

// ─── Profanity (basic list — Spanish + English) ──────────────────────────

const PROFANITY_PATTERNS: RegExp[] = [
  // English
  /\b(fuck|shit|damn|ass|bitch|dick|cock|cunt|bastard)\w*/gi,
  // Spanish
  /\b(puta|mierda|verga|culo|pendejo|cabr[oó]n|chingar|joder|hijo\s+de\s+puta|malparido|cerote|cul[eé]ro)\w*/gi,
]

// ─── Core filter ──────────────────────────────────────────────────────────

export interface FilterResult {
  /** The filtered message content (with redactions applied) */
  filtered: string
  /** Whether any content was redacted */
  wasFiltered: boolean
  /** Types of content that were detected */
  detectedTypes: ContentType[]
}

type ContentType = 'phone' | 'email' | 'url' | 'social' | 'profanity'

/**
 * Filter message content for disintermediation attempts and profanity.
 *
 * Returns the filtered content with sensitive data replaced by [***],
 * plus metadata about what was detected.
 */
export function filterMessage(content: string): FilterResult {
  let filtered = content
  const detectedTypes: ContentType[] = []

  const apply = (patterns: readonly RegExp[], type: ContentType): void => {
    const result = redactPatterns(filtered, patterns, REDACTION)
    filtered = result.text
    if (result.matched) detectedTypes.push(type)
  }

  // Emails before phones: an address whose local part looks like a number
  // (`7654.3210@mail.com`) must be redacted whole, not split by the phone
  // pass into a redacted prefix plus a readable domain.
  apply(PII_EMAIL_PATTERNS, 'email')
  apply(PII_PHONE_PATTERNS, 'phone')
  apply(URL_PATTERNS, 'url')
  apply(SOCIAL_PATTERNS, 'social')
  apply(PROFANITY_PATTERNS, 'profanity')

  return {
    filtered,
    wasFiltered: detectedTypes.length > 0,
    detectedTypes,
  }
}
